import React from "react";

export default function Privacy() {
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  return (
    <main className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-600 mb-8">
            <strong>Last updated:</strong> {today}
          </p>

          <div className="prose prose-lg max-w-none">
            {/* 1. Introduction */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-700 mb-4">
                Experience ("we", "our", or "us") provides a mood and experience
                tracking application (the "App"). This Privacy Policy explains
                how personal information is collected, used, disclosed,
                retained, and protected when using the App. By using the App,
                users agree to this Policy. If users do not agree, they should
                not use the App.
              </p>
            </section>

            {/* 1.1 Controller & Contact */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1.1 Data Controller & Contact
              </h2>
              <p className="text-gray-700 mb-4">
                Data Controller: Ruslan Eskov, individual developer.
                <br />
                Registered address: Turu 8, 51004 Tartu, Estonia.
                <br />
                Contact:{" "}
                <a className="underline" href="mailto:support@experienceapp.io">
                  support@experienceapp.io
                </a>
                .<br />
              </p>
            </section>

            {/* 2. Information We Collect */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Information We Collect
              </h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                2.1 Account & Contact Information
              </h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>Email address.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                2.2 App Usage & Device Information
              </h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  Device details (OS, device type, identifiers), app version,
                  crash/performance data.
                </li>
                <li>
                  Usage data (features used, timestamps, in-app interactions).
                </li>
                <li>
                  IP address and server logs for security and fraud prevention.
                </li>
                <li>Push notification token to deliver notifications.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                2.3 Health, Wellness & Sensitive Data
              </h3>
              <p className="text-gray-700 mb-2">
                Collected only with explicit user action:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>Experience entries, feelings, causes.</li>
                <li>Derived analytics and insights generated in-app.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                2.4 Team & Collaboration Data
              </h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>Team membership and roles (member, lead).</li>
                <li>Audit logs (invites, removals).</li>
              </ul>
            </section>

            {/* 3. Legal Bases (GDPR/UK GDPR) */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. Legal Bases for Processing (where applicable)
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  <strong>Consent:</strong> processing mood/experience entries,
                  push notifications, research/analytics beyond strictly
                  necessary.
                </li>
                <li>
                  <strong>Contract:</strong> to provide and operate the App and
                  requested features.
                </li>
                <li>
                  <strong>Legitimate Interests:</strong> security, fraud
                  prevention, service improvement (balanced against user
                  rights).
                </li>
                <li>
                  <strong>Legal Obligation:</strong> compliance with applicable
                  laws and requests from authorities.
                </li>
              </ul>
              <p className="text-gray-700 mb-4">
                Users may withdraw consent at any time in settings or by
                contacting the address above; withdrawal does not affect prior
                lawful processing.
              </p>
              <p className="text-gray-700 mb-4">
                Some entries may reveal health-related or other special category
                data. Such processing is performed only with users’ explicit
                consent (GDPR Art. 9(2)(a)), collected via an in-app consent
                screen and withdrawable at any time in Settings.
              </p>
            </section>

            {/* 4. How We Use Information */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. How We Use Information
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>Provide, operate, maintain, and secure the App.</li>
                <li>
                  Process entries to generate personal insights and trends.
                </li>
                <li>
                  Send check-ins and reminders (push notifications, if enabled).
                </li>
                <li>Monitor performance, fix bugs, and improve features.</li>
                <li>Communicate about updates, security, and support.</li>
              </ul>
            </section>

            {/* 5. TeamsOverview Features & Sharing Controls */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Team Features & Sharing Controls
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  Role-based access controls: only leads can view/add/remove
                  team members and manage shared cause lists.
                </li>
                <li>
                  All individual entries anonimously shared with the lead.
                </li>
              </ul>
            </section>

            {/* 6. Push NotificationSettingsPage */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Push Notifications
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  <strong>Token Collection:</strong> push tokens are collected
                  to deliver notifications.
                </li>
                <li>
                  <strong>Permission:</strong> permission is requested before
                  sending notifications; can be withdrawn in device settings.
                </li>
                <li>
                  <strong>Purpose:</strong> check-ins and service updates.
                </li>
                <li>
                  <strong>Provider:</strong> Firebase Cloud Messaging (FCM) or
                  equivalent.
                </li>
              </ul>
            </section>

            {/* 7. Sharing & Disclosure */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Information Sharing and Disclosure
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  <strong>Service Providers:</strong> hosting, analytics, and
                  communications providers (e.g., cloud hosting, Firebase/FCM).
                  Providers are bound by contracts and confidentiality and act
                  only on instructions.
                </li>
                <li>
                  <strong>Legal:</strong> disclosures required by law or to
                  protect rights, safety, and security.
                </li>
                <li>
                  <strong>Business Transfers:</strong> in case of merger,
                  acquisition, or asset sale, with notice and continued
                  protections.
                </li>
                <li>
                  <strong>Aggregated/De-identified Data:</strong> may be shared
                  without identifying users.
                </li>
              </ul>
            </section>

            {/* 8. Security */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Data Security
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  Encryption in transit and at rest for production systems.
                </li>
                <li>
                  Secure authentication, role-based access, least-privilege, MFA
                  for admin accounts, and audit logging.
                </li>
                <li>
                  Vulnerability management and incident response procedures.
                </li>
              </ul>
              <p className="text-gray-700 mb-2">
                In the event of a personal data breach, affected users and/or
                authorities will be notified as required by law, typically
                within 72 hours of becoming aware.
              </p>
              <p className="text-gray-700">
                No method of transmission or storage is 100% secure.
              </p>
            </section>

            {/* 9. Data Retention */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Data Retention
              </h2>
              <p className="text-gray-700 mb-2">
                Personal data is retained for the duration of the account and as
                needed to provide the App. Upon account deletion, personal data
                is scheduled for deletion within 30 days, subject to legal
                retention obligations.
              </p>
              <table className="table-auto w-full text-left my-4">
                <caption className="sr-only">Data retention periods</caption>
                <thead>
                  <tr>
                    <th scope="col" className="pr-4">
                      Data
                    </th>
                    <th scope="col" className="pr-4">
                      Retention
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Account info.</td>
                    <td>Lifetime of account; 30 days after deletion.</td>
                  </tr>
                  <tr>
                    <td>Mood/experience entries.</td>
                    <td>Until user deletes or account deletion.</td>
                  </tr>
                  <tr>
                    <td>Audit logs.</td>
                    <td>Up to 12 months.</td>
                  </tr>
                  <tr>
                    <td>Backups.</td>
                    <td>Encrypted; rolling retention up to 30–90 days.</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* 10. International Transfers */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. International Data Transfers
              </h2>
              <p className="text-gray-700 mb-4">
                Data may be processed in countries other than the user’s
                country. Where required, appropriate safeguards are used (e.g.,
                Standard Contractual Clauses, adequacy decisions). Additional
                technical and organizational measures are applied for
                cross-border transfers.
              </p>
            </section>

            {/* 11. Children’s Privacy */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Children’s Privacy
              </h2>
              <p className="text-gray-700 mb-4">
                The App is not intended for children under 13 years of age, and
                personal information from children under 13 is not knowingly
                collected. For users in the EEA/UK, where local law may require
                a higher age of consent (up to 16), processing of personal data
                for users under the applicable age requires verified
                parental/guardian consent. Parents/guardians who believe a child
                has provided personal data should contact the email above so it
                can be deleted.
              </p>
              <p className="text-gray-700 mb-4">
                Age-gate measures are implemented. Suspected under-age accounts
                are restricted and removed unless verified parental consent is
                obtained.
              </p>
            </section>

            {/* 12. Your Rights */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Your Rights
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  Access, correction, deletion, and portability of personal
                  data.
                </li>
                <li>
                  Restriction or objection to certain processing (including for
                  legitimate interests).
                </li>
                <li>
                  Withdrawal of consent at any time where processing is based on
                  consent.
                </li>
                <li>
                  Right to lodge a complaint with a supervisory authority
                  (Estonian Data Protection Inspectorate / Andmekaitse
                  Inspektsioon).
                </li>
              </ul>
              <p className="text-gray-700 mb-2">
                Requests are verified via the signed-in account or email
                confirmation. Responses are provided within one month,
                extendable by two months for complex requests.
              </p>
              <p className="text-gray-700">
                Submit requests via in-app settings or{" "}
                <a className="underline" href="mailto:support@experienceapp.io">
                  support@experienceapp.io
                </a>
                .
              </p>
            </section>

            {/* 13. Region-Specific Notices */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Region-Specific Notices
              </h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                California (CPRA)
              </h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  Categories collected: identifiers; internet/network activity;
                  geolocation (approximate, if enabled); in-app content;
                  inferences. Sensitive personal information includes
                  mood/health-related entries.
                </li>
                <li>
                  Purposes: as described in this Policy. No selling of personal
                  information. No sharing for cross-context behavioral
                  advertising without user consent.
                </li>
                <li>
                  Rights: know/access, delete, correct, opt-out of sale/sharing,
                  limit use of sensitive information, non-discrimination.
                </li>
              </ul>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                EEA/UK
              </h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 ml-4">
                <li>
                  Controller, legal bases, transfer safeguards, and rights per
                  sections above.
                </li>
              </ul>
            </section>

            {/* 14. Data Sources & Automated Decisions */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                14. Data Sources & Automated Decisions
              </h2>
              <p className="text-gray-700 mb-4">
                Personal data primarily comes from users. Limited metrics may be
                derived automatically to provide insights. No solely automated
                decisions with legal or similarly significant effects are made.
              </p>
            </section>

            {/* 15. Cookies/Tracking */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                15. Cookies & Similar Technologies
              </h2>
              <p className="text-gray-700 mb-4">
                If the App or web dashboard uses cookies or similar
                technologies, a separate Cookie Notice describes types,
                purposes, and choices. Analytics and performance cookies are
                used only with consent where required.
              </p>
            </section>

            {/* 16. Changes */}
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                16. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 mb-4">
                Material changes will be notified via in-app notice or email
                (where appropriate), with the “Last updated” date adjusted.
                Continued use after the effective date indicates acceptance.
              </p>
            </section>

            {/* 17. Contact */}
            <section className="mb-4">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                17. Contact
              </h2>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong>{" "}
                  <a
                    className="underline"
                    href="mailto:support@experienceapp.io"
                  >
                    support@experienceapp.io
                  </a>
                  <br />
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
